import * as React from 'react';
import styled from 'styled-components';
import { Link, Paragraph, Text } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
// eslint-disable-next-line no-restricted-imports
import { colors, spaces, typography } from '@99designs/emu/settings';
import { Desktop } from '@99designs/frontend-components/components/Desktop';
import { Mobile } from '@99designs/frontend-components/components/Mobile';
import Pill from '@99designs/frontend-components/components/Pill/Pill';
import { FormattedMessage } from '@99designs/frontend-components/i18n';
import InteractiveAvatar from '../InteractiveAvatar';
import LocalTime from '../LocalTime';
import OnlineStatus from '../OnlineStatus/OnlineStatus';
import { isDesignerOnline } from '../isDesignerOnline';

const StyledDesignerMetaHeader = styled.div`
    grid-area: header;
    display: grid;
    grid-template-columns: auto auto 1fr;
    grid-template-areas:
        'avatar name name name'
        'avatar online-status local-time local-time'
        '. language language language';

    padding-bottom: ${spaces['0.75bu']};
`;

const StyledDesignerMetaHeaderNarrow = styled(StyledDesignerMetaHeader)`
    grid-template-areas:
        'avatar name name name'
        'avatar online-status online-status online-status'
        '. local-time . language';
    ${mediaQuery.only('small')} {
        grid-template-areas:
            'avatar name name name'
            'avatar online-status online-status online-status'
            '. local-time local-time local-time'
            '. language language language';
    }
`;

const AvatarWrapper = styled.div`
    grid-area: avatar;
    padding-right: ${spaces['1bu']};
    align-self: center;
`;

const DesignerName = styled.div`
    grid-area: name;
    align-self: end;
`;

const StyledOnlineStatus = styled.div`
    grid-area: online-status;
    line-height: ${typography.lineHeights.root};
    font-size: ${typography.typeScale.size2}px;
    text-align: left;
    align-self: center;
    margin-right: ${spaces['0.75bu']};
`;

const StyledLocalTime = styled.div`
    grid-area: local-time;
    align-self: end;
    line-height: ${typography.lineHeights.root};
    font-size: ${typography.typeScale.size2}px;
    color: ${colors.grey700};
`;

const LanguageWrapper = styled.div`
    grid-area: language;
    align-self: start;
    font-size: ${typography.typeScale.size2}px;
    ${mediaQuery.minimum('normal')} {
        margin-top: ${spaces['0.5bu']};
    }
    ${mediaQuery.only('small')} {
        margin-top: ${spaces['0.25bu']};
    }
`;

interface DesignerMetaHeaderBaseProps {
    designer: {
        id: string;
        displayName: string;
        profile: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            lastSeenAt: any;
            isAvailable: boolean;
            utcOffset: string;
        };
    };
    onRemove?: (userId: number) => void;
    paragraphSize: 'regular' | 'small';
    avatarSize: 'extra-small' | 'small' | 'medium';
    spokenLanguage?: string;
    linkToProfile?: boolean;
}

function DesignerMetaHeaderBase({
    designer,
    onRemove,
    paragraphSize,
    avatarSize,
    spokenLanguage,
    linkToProfile = false,
}: DesignerMetaHeaderBaseProps) {
    const isOnlineNow = isDesignerOnline(designer.profile.lastSeenAt);

    return (
        <>
            <AvatarWrapper>
                <InteractiveAvatar
                    userId={designer.id}
                    online={isOnlineNow}
                    onRemoveClick={onRemove}
                    size={avatarSize}
                />
            </AvatarWrapper>
            <DesignerName data-testid="designer-meta-header-name">
                {linkToProfile ? (
                    <Link
                        href={`/profiles/${designer.id}`}
                        underline="hover"
                        color="grey500"
                        target="_blank"
                    >
                        <Text data-qm-block size="size2">
                            {designer.displayName}
                        </Text>
                    </Link>
                ) : (
                    <Text data-qm-block size="size2">
                        {designer.displayName}
                    </Text>
                )}
            </DesignerName>
            <StyledOnlineStatus>
                <Paragraph as="div" size={paragraphSize}>
                    {designer.profile.isAvailable ? (
                        <OnlineStatus
                            isOnline={isOnlineNow}
                            lastSeenAt={designer.profile.lastSeenAt}
                        />
                    ) : (
                        <Pill variations={['caution']}>{__('Unavailable')}</Pill>
                    )}
                </Paragraph>
            </StyledOnlineStatus>
            <StyledLocalTime>
                <Paragraph as="div" size={paragraphSize}>
                    <LocalTime utcOffset={designer.profile.utcOffset} />
                </Paragraph>
            </StyledLocalTime>
            {spokenLanguage && (
                <LanguageWrapper>
                    <Pill>
                        <FormattedMessage
                            message={__('Speaks {language}')}
                            values={{
                                language: spokenLanguage,
                            }}
                        />
                    </Pill>
                </LanguageWrapper>
            )}
        </>
    );
}

export default function DesignerMetaHeader({ ...props }: DesignerMetaHeaderBaseProps) {
    return (
        <>
            <Desktop>
                <DesignerMetaHeaderStandard {...props} />
            </Desktop>
            <Mobile>
                <DesignerMetaHeaderNarrow {...props} />
            </Mobile>
        </>
    );
}

const DesignerMetaHeaderStandard = (props: DesignerMetaHeaderBaseProps) => (
    <StyledDesignerMetaHeader>
        <DesignerMetaHeaderBase {...props} />
    </StyledDesignerMetaHeader>
);

const DesignerMetaHeaderNarrow = (props: DesignerMetaHeaderBaseProps) => (
    <StyledDesignerMetaHeaderNarrow>
        <DesignerMetaHeaderBase {...props} />
    </StyledDesignerMetaHeaderNarrow>
);
